<template>
    <div class="event-registration-page">
        <!-- Loading State -->
        <div v-if="loading" class="er-loading-state">
            <b-spinner variant="light" label="Loading..." style="width: 3rem; height: 3rem;" />
            <p class="mt-3 text-white">Loading event details...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="er-error-state">
            <div class="er-error-card">
                <i class="fa fa-exclamation-circle" style="font-size: 3.5rem; color: #e74c3c;"></i>
                <h3 class="mt-3">Event Not Found</h3>
                <p class="text-muted">{{ error }}</p>
            </div>
        </div>

        <!-- Success State -->
        <div v-else-if="registrationComplete" class="er-success-wrapper">
            <div class="er-success-card">
                <div class="er-success-icon">
                    <i class="fa fa-check-circle" style="font-size: 4rem; color: #27ae60;"></i>
                </div>
                <h2 class="mt-3">You're Registered!</h2>
                <p class="text-muted">{{ registrationResult.event_name }}</p>

                <div v-if="registrationResult.rsvp_code" class="er-code-box my-4">
                    <p class="mb-1 small text-muted">Your RSVP Code</p>
                    <h2 class="mb-0 font-weight-bold" style="color: #667eea; letter-spacing: 3px;">
                        {{ registrationResult.rsvp_code }}
                    </h2>
                    <p class="mt-2 small text-muted">Use this code with your email to log into the portal</p>
                </div>

                <div v-if="registrationResult.payment_status === 'pending'" class="er-payment-notice mb-4">
                    <i class="fa fa-clock-o mr-2"></i>
                    <strong>Payment Pending</strong> &mdash; The organizer will contact you with payment instructions.
                    <div v-if="registrationResult.ticket_tier" class="mt-1">
                        Amount: <strong>{{ formatPrice(registrationResult.ticket_tier.price, registrationResult.ticket_tier.currency) }}</strong>
                    </div>
                </div>

                <div v-if="registrationResult.ticket_tier" class="mb-3 text-muted small">
                    Ticket: <strong>{{ registrationResult.ticket_tier.name }}</strong>
                </div>

                <div v-if="registrationResult.registration_confirmation_html"
                     class="er-confirmation-html mb-4"
                     v-html="registrationResult.registration_confirmation_html">
                </div>
            </div>
        </div>

        <!-- Main Event Page -->
        <div v-else-if="eventData" class="er-main">

            <!-- CUSTOM DESIGN MODE -->
            <template v-if="hasCustomPage">
                <div class="er-custom-page" v-html="hasEmbeddedForm ? customPageBefore : renderedPageHtml"></div>
            </template>

            <!-- DEFAULT MODE: Built-in hero + info bar + details -->
            <template v-if="!hasCustomPage">
                <!-- Hero Section -->
                <section class="er-hero" :style="heroStyle">
                    <div class="er-hero-overlay">
                        <div class="er-hero-content">
                            <div v-if="eventData.event.organizer_logo_url" class="er-organizer-logo mb-3">
                                <img :src="eventData.event.organizer_logo_url" alt="Organizer" />
                            </div>
                            <h1 class="er-hero-title">{{ eventData.event.name }}</h1>
                            <p v-if="eventData.event.short_description" class="er-hero-subtitle">
                                {{ eventData.event.short_description }}
                            </p>
                            <p v-if="eventData.event.organizer_name" class="er-hero-organizer">
                                By {{ eventData.event.organizer_name }}
                            </p>
                        </div>
                    </div>
                </section>

                <!-- Event Info Bar -->
                <section class="er-info-bar">
                    <div class="er-info-bar-inner">
                        <div class="er-info-items">
                            <div v-if="eventData.event.event_date" class="er-info-item">
                                <i class="fa fa-calendar"></i>
                                <span>{{ formattedDate }}</span>
                            </div>
                            <div v-if="eventData.event.venue" class="er-info-item">
                                <i class="fa fa-map-marker"></i>
                                <span>{{ eventData.event.venue }}</span>
                            </div>
                            <div v-if="eventData.event.contact_email" class="er-info-item">
                                <i class="fa fa-envelope"></i>
                                <span>{{ eventData.event.contact_email }}</span>
                            </div>
                        </div>
                        <div class="er-info-cta">
                            <b-button
                                v-if="eventData.registration_open && eventData.has_capacity"
                                variant="light"
                                class="er-register-cta-btn"
                                @click="scrollToTickets"
                            >
                                Register Now
                            </b-button>
                            <span v-else-if="!eventData.has_capacity" class="text-white-50 font-weight-bold">
                                Event Full
                            </span>
                        </div>
                    </div>
                </section>

                <!-- Event Details Section -->
                <section class="er-details-section">
                    <div class="er-details-inner">
                        <div class="er-details-grid">
                            <div class="er-details-main">
                                <h3 class="er-section-title">About This Event</h3>
                                <div v-if="eventData.event.event_description"
                                     class="er-description"
                                     v-html="eventData.event.event_description">
                                </div>
                                <p v-else class="text-muted">No additional details provided.</p>
                            </div>

                            <div class="er-details-sidebar">
                                <div class="er-sidebar-card">
                                    <h5 class="er-sidebar-title">Event Details</h5>
                                    <div class="er-sidebar-items">
                                        <div v-if="eventData.event.event_date" class="er-sidebar-item">
                                            <i class="fa fa-calendar-o"></i>
                                            <div>
                                                <strong>Date & Time</strong>
                                                <p>{{ formattedDate }}</p>
                                                <p v-if="formattedEndDate">to {{ formattedEndDate }}</p>
                                            </div>
                                        </div>
                                        <div v-if="eventData.event.venue" class="er-sidebar-item">
                                            <i class="fa fa-map-marker"></i>
                                            <div>
                                                <strong>Location</strong>
                                                <p>{{ eventData.event.venue }}</p>
                                                <p v-if="eventData.event.address">{{ eventData.event.address }}</p>
                                            </div>
                                        </div>
                                        <div v-if="eventData.event.organizer_name" class="er-sidebar-item">
                                            <i class="fa fa-user"></i>
                                            <div>
                                                <strong>Organizer</strong>
                                                <p>{{ eventData.event.organizer_name }}</p>
                                            </div>
                                        </div>
                                        <div v-if="eventData.event.contact_email" class="er-sidebar-item">
                                            <i class="fa fa-envelope-o"></i>
                                            <div>
                                                <strong>Contact</strong>
                                                <p>{{ eventData.event.contact_email }}</p>
                                            </div>
                                        </div>
                                        <div v-if="eventData.event.rsvp_deadline || eventData.event.registration_deadline" class="er-sidebar-item">
                                            <i class="fa fa-clock-o"></i>
                                            <div>
                                                <strong>Deadline</strong>
                                                <p>{{ formattedDeadline }}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="er-share-link mt-3">
                                        <b-button variant="outline-secondary" size="sm" block @click="copyEventLink">
                                            <i class="fa fa-link mr-1"></i> {{ linkCopied ? 'Copied!' : 'Copy Event Link' }}
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </template>

            <!-- Registration Closed -->
            <section v-if="!eventData.registration_open" class="er-closed-section" id="tickets">
                <div class="er-closed-card">
                    <i class="fa fa-lock" style="font-size: 2.5rem; color: #95a5a6;"></i>
                    <h4 class="mt-3">Registration Closed</h4>
                    <p class="text-muted">{{ eventData.registration_closed_reason || 'Registration is not available.' }}</p>
                </div>
            </section>

            <!-- Capacity Full -->
            <section v-else-if="!eventData.has_capacity" class="er-closed-section" id="tickets">
                <div class="er-closed-card">
                    <i class="fa fa-users" style="font-size: 2.5rem; color: #e67e22;"></i>
                    <h4 class="mt-3">Event is Full</h4>
                    <p class="text-muted">This event has reached its maximum capacity.</p>
                </div>
            </section>

            <!-- Ticket Selection + Registration Form -->
            <section v-else class="er-tickets-section" id="tickets">
                <div class="er-tickets-inner">
                    <h3 class="er-section-title text-center mb-4">Select Your Ticket</h3>

                    <!-- Tier Cards -->
                    <div class="er-tier-grid">
                        <div
                            v-for="tier in eventData.ticket_tiers"
                            :key="tier.id"
                            class="er-tier-card"
                            :class="{
                                'er-tier-selected': selectedTierId === tier.id,
                                'er-tier-disabled': tier.is_sold_out || !tier.is_on_sale,
                            }"
                            @click="selectTier(tier)"
                        >
                            <div class="er-tier-header">
                                <h5 class="er-tier-name">{{ tier.name }}</h5>
                                <div class="er-tier-price">
                                    <span v-if="tier.type === 'free'" class="er-price-free">Free</span>
                                    <span v-else-if="tier.type === 'paid'">
                                        {{ formatPrice(tier.price, tier.currency) }}
                                    </span>
                                </div>
                            </div>
                            <p v-if="tier.description" class="er-tier-desc">{{ tier.description }}</p>
                            <div class="er-tier-footer">
                                <span v-if="tier.is_sold_out" class="er-tier-badge er-badge-sold-out">
                                    Sold Out
                                </span>
                                <span v-else-if="!tier.is_on_sale" class="er-tier-badge er-badge-unavailable">
                                    Not Available
                                </span>
                                <span v-else-if="tier.available_quantity !== null" class="er-tier-availability">
                                    {{ tier.available_quantity }} remaining
                                </span>
                                <b-button
                                    v-if="!tier.is_sold_out && tier.is_on_sale"
                                    :variant="selectedTierId === tier.id ? 'primary' : 'outline-primary'"
                                    size="sm"
                                    class="mt-2"
                                    @click.stop="selectTier(tier)"
                                >
                                    {{ selectedTierId === tier.id ? 'Selected' : 'Select' }}
                                </b-button>
                            </div>
                        </div>
                    </div>

                    <!-- No tiers -->
                    <div v-if="!eventData.ticket_tiers || eventData.ticket_tiers.length === 0" class="text-center text-muted py-4">
                        <p>No tickets available at this time.</p>
                    </div>

                    <!-- Registration Form (slides in) -->
                    <transition name="er-slide">
                        <div v-if="selectedTierId" class="er-form-section mt-4">
                            <div class="er-form-card">
                                <h4 class="mb-4">Complete Your Registration</h4>

                                <b-form @submit.prevent="submitRegistration">
                                    <b-form-group label="Full Name *">
                                        <b-form-input
                                            v-model="form.name"
                                            placeholder="Enter your full name"
                                            required
                                            :state="formErrors.name ? false : null"
                                        />
                                        <b-form-invalid-feedback>{{ formErrors.name }}</b-form-invalid-feedback>
                                    </b-form-group>

                                    <b-form-group label="Email Address *">
                                        <b-form-input
                                            v-model="form.email"
                                            type="email"
                                            placeholder="Enter your email"
                                            required
                                            :state="formErrors.email ? false : null"
                                        />
                                        <b-form-invalid-feedback>{{ formErrors.email }}</b-form-invalid-feedback>
                                    </b-form-group>

                                    <b-form-group label="Phone Number">
                                        <b-form-input
                                            v-model="form.phone"
                                            placeholder="Enter your phone number (optional)"
                                        />
                                    </b-form-group>

                                    <!-- Additional Fields -->
                                    <PublicAdditionalFields
                                        v-if="hasAdditionalFields"
                                        ref="additionalFields"
                                        :fields="eventData.field_definitions"
                                        v-model="additionalFieldValues"
                                        theme="light"
                                    />

                                    <!-- Paid Tier Notice -->
                                    <div v-if="selectedTierRequiresPayment" class="er-payment-info mb-4">
                                        <i class="fa fa-info-circle mr-2"></i>
                                        Payment of <strong>{{ formatPrice(selectedTier.price, selectedTier.currency) }}</strong>
                                        is required. You'll receive payment instructions after registration.
                                    </div>

                                    <b-button
                                        type="submit"
                                        variant="primary"
                                        size="lg"
                                        block
                                        class="er-submit-btn"
                                        :disabled="submitting"
                                    >
                                        <b-spinner v-if="submitting" small class="mr-2" />
                                        Complete Registration
                                    </b-button>
                                </b-form>
                            </div>
                        </div>
                    </transition>
                </div>
            </section>

            <!-- Custom page: after-form content -->
            <div v-if="hasCustomPage && hasEmbeddedForm && customPageAfter"
                 class="er-custom-page" v-html="customPageAfter"></div>
        </div>
    </div>
</template>

<script>
import PublicAdditionalFields from '@/components/EventInvitations/PublicAdditionalFields.vue'
import dateTimeMixins from '@/mixins/dateTimeMixins'

export default {
    name: 'EventRegistration',
    mixins: [dateTimeMixins],
    components: { PublicAdditionalFields },
    data() {
        return {
            loading: true,
            error: null,
            eventData: null,
            selectedTierId: null,
            form: {
                name: '',
                email: '',
                phone: '',
            },
            formErrors: {},
            additionalFieldValues: {},
            submitting: false,
            registrationComplete: false,
            registrationResult: {},
            linkCopied: false,
        }
    },
    computed: {
        token() {
            return this.$route.params.token
        },
        heroImage() {
            if (!this.eventData) return null
            return this.eventData.event.hero_image_url || this.eventData.event.flyer_url
        },
        heroStyle() {
            if (this.heroImage) {
                return {
                    backgroundImage: `url(${this.heroImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }
            }
            return {}
        },
        formattedDate() {
            return this.formatDate(this.eventData?.event?.event_date)
        },
        formattedEndDate() {
            return this.formatDate(this.eventData?.event?.event_end_date)
        },
        formattedDeadline() {
            const d = this.eventData?.event?.rsvp_deadline || this.eventData?.event?.registration_deadline
            return this.formatDate(d)
        },
        hasAdditionalFields() {
            return Array.isArray(this.eventData?.field_definitions) && this.eventData.field_definitions.length > 0
        },
        selectedTier() {
            if (!this.selectedTierId || !this.eventData?.ticket_tiers) return null
            return this.eventData.ticket_tiers.find(t => t.id === this.selectedTierId)
        },
        selectedTierRequiresPayment() {
            return this.selectedTier?.type === 'paid' && parseFloat(this.selectedTier?.price) > 0
        },
        hasCustomPage() {
            return !!this.eventData?.event?.event_page_html
        },
        hasEmbeddedForm() {
            return this.hasCustomPage && /\{\{\s*registration_form\s*\}\}/.test(this.renderedPageHtml)
        },
        customPageBefore() {
            if (!this.hasEmbeddedForm) return ''
            return this.renderedPageHtml.split(/\{\{\s*registration_form\s*\}\}/)[0] || ''
        },
        customPageAfter() {
            if (!this.hasEmbeddedForm) return ''
            return this.renderedPageHtml.split(/\{\{\s*registration_form\s*\}\}/)[1] || ''
        },
        renderedPageHtml() {
            let html = this.eventData?.event?.event_page_html || ''
            if (!html) return ''
            const ev = this.eventData.event
            const vars = {
                event_name: ev.name || '',
                event_date: this.formattedDate || '',
                event_end_date: this.formattedEndDate || '',
                venue: ev.venue || '',
                address: ev.address || '',
                event_description: ev.event_description || '',
                short_description: ev.short_description || '',
                organizer_name: ev.organizer_name || '',
                contact_email: ev.contact_email || '',
                rsvp_deadline: this.formattedDeadline || '',
                flyer_url: ev.flyer_url || '',
                hero_image_url: ev.hero_image_url || ev.flyer_url || '',
            }
            Object.keys(vars).forEach(key => {
                html = html.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), vars[key])
            })
            return html
        },
    },
    async mounted() {
        await this.loadEvent()
    },
    methods: {
        formatDate(dateStr) {
            if (!dateStr) return ''
            return this.displayStoredDateTime(dateStr)
        },
        formatPrice(price, currency) {
            const num = parseFloat(price)
            if (isNaN(num) || num === 0) return 'Free'
            const formatted = num.toFixed(2)
            return `${currency || 'USD'} ${formatted}`
        },
        async loadEvent() {
            this.loading = true
            this.error = null
            try {
                const data = await this.$store.dispatch('eventInvitation/fetchPublicEvent', this.token)
                this.eventData = data
                if (data.ticket_tiers && data.ticket_tiers.length === 1 && !data.ticket_tiers[0].is_sold_out) {
                    this.selectedTierId = data.ticket_tiers[0].id
                }
            } catch (err) {
                this.error = err?.response?.data?.error?.message || 'This event link is invalid or the event does not exist.'
            } finally {
                this.loading = false
            }
        },
        selectTier(tier) {
            if (tier.is_sold_out || !tier.is_on_sale) return
            this.selectedTierId = tier.id
            this.$nextTick(() => {
                const formEl = document.querySelector('.er-form-section')
                if (formEl) {
                    formEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            })
        },
        scrollToTickets() {
            const el = document.getElementById('tickets')
            if (el) el.scrollIntoView({ behavior: 'smooth' })
        },
        validateForm() {
            this.formErrors = {}
            if (!this.form.name || !this.form.name.trim()) {
                this.formErrors.name = 'Full name is required.'
            }
            if (!this.form.email || !this.form.email.trim()) {
                this.formErrors.email = 'Email address is required.'
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
                this.formErrors.email = 'Please enter a valid email address.'
            }
            return Object.keys(this.formErrors).length === 0
        },
        async submitRegistration() {
            if (!this.validateForm()) return

            if (this.hasAdditionalFields && this.$refs.additionalFields) {
                const isValid = this.$refs.additionalFields.validate()
                if (!isValid) return
            }

            this.submitting = true
            try {
                const payload = {
                    token: this.token,
                    name: this.form.name.trim(),
                    email: this.form.email.trim().toLowerCase(),
                    phone: this.form.phone?.trim() || null,
                    ticket_tier_id: this.selectedTierId,
                }
                if (this.hasAdditionalFields && Object.keys(this.additionalFieldValues).length > 0) {
                    payload.additional_fields = this.additionalFieldValues
                }
                const result = await this.$store.dispatch('eventInvitation/registerForEvent', payload)
                this.registrationResult = result
                this.registrationComplete = true
                window.scrollTo({ top: 0, behavior: 'smooth' })
            } catch (err) {
                const msg = err?.response?.data?.error?.message || 'Registration failed. Please try again.'
                this.$bvToast.toast(msg, {
                    title: 'Registration Error',
                    variant: 'danger',
                    solid: true,
                })
            } finally {
                this.submitting = false
            }
        },
        copyEventLink() {
            const url = window.location.href
            navigator.clipboard.writeText(url).then(() => {
                this.linkCopied = true
                setTimeout(() => { this.linkCopied = false }, 2000)
            })
        },
    },
}
</script>

<style scoped>
.event-registration-page {
    min-height: 100vh;
    background: #f8f9fa;
}

/* Loading / Error States */
.er-loading-state {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea, #764ba2);
}
.er-error-state {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
}
.er-error-card {
    text-align: center;
    padding: 60px 40px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    max-width: 450px;
}

/* Success State */
.er-success-wrapper {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%);
    padding: 20px;
}
.er-success-card {
    text-align: center;
    padding: 48px 40px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    max-width: 520px;
    width: 100%;
}
.er-code-box {
    background: #f0f4ff;
    border: 2px dashed #667eea;
    border-radius: 12px;
    padding: 20px;
}
.er-payment-notice {
    background: #fff8e1;
    border: 1px solid #ffe082;
    border-radius: 8px;
    padding: 16px;
    color: #e65100;
    text-align: left;
}

/* Custom Page (full designer HTML) */
.er-custom-page {
    overflow-x: hidden;
}
.er-custom-page ::v-deep img {
    max-width: 100%;
    height: auto;
}

/* Hero */
.er-hero {
    position: relative;
    min-height: 400px;
    display: flex;
    align-items: flex-end;
    background: linear-gradient(135deg, #667eea, #764ba2);
}
.er-hero-overlay {
    width: 100%;
    padding: 60px 24px 40px;
    background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 60%, transparent 100%);
}
.er-hero-content {
    max-width: 900px;
    margin: 0 auto;
    color: white;
}
.er-organizer-logo img {
    height: 48px;
    border-radius: 8px;
    background: white;
    padding: 4px;
}
.er-hero-title {
    font-size: 2.5rem;
    font-weight: 800;
    line-height: 1.2;
    margin-bottom: 8px;
    text-shadow: 0 2px 8px rgba(0,0,0,0.3);
}
.er-hero-subtitle {
    font-size: 1.15rem;
    opacity: 0.9;
    margin-bottom: 4px;
}
.er-hero-organizer {
    font-size: 0.95rem;
    opacity: 0.75;
}

/* Info Bar */
.er-info-bar {
    background: #2c3e50;
    color: white;
    padding: 16px 24px;
    position: sticky;
    top: 0;
    z-index: 100;
}
.er-info-bar-inner {
    max-width: 900px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
}
.er-info-items {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
}
.er-info-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
}
.er-info-item i {
    opacity: 0.7;
}
.er-register-cta-btn {
    font-weight: 600;
    border-radius: 8px;
    padding: 8px 24px;
    color: #2c3e50;
}

/* Details Section */
.er-details-section {
    padding: 48px 24px;
}
.er-details-inner {
    max-width: 900px;
    margin: 0 auto;
}
.er-details-grid {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 32px;
}
.er-section-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 20px;
}
.er-description {
    line-height: 1.8;
    color: #555;
}
.er-sidebar-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    position: sticky;
    top: 80px;
}
.er-sidebar-title {
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid #f0f0f0;
}
.er-sidebar-item {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
}
.er-sidebar-item i {
    color: #667eea;
    font-size: 1.1rem;
    margin-top: 2px;
    width: 20px;
    text-align: center;
}
.er-sidebar-item strong {
    display: block;
    font-size: 0.85rem;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
}
.er-sidebar-item p {
    margin: 0;
    font-size: 0.95rem;
    color: #333;
}

/* Closed Section */
.er-closed-section {
    padding: 60px 24px;
    text-align: center;
}
.er-closed-card {
    max-width: 450px;
    margin: 0 auto;
    background: white;
    border-radius: 16px;
    padding: 48px 40px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}

/* Tickets Section */
.er-tickets-section {
    padding: 48px 24px 60px;
    background: #f0f2f5;
}
.er-tickets-inner {
    max-width: 900px;
    margin: 0 auto;
}
.er-tier-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
}
.er-tier-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    border: 2px solid #e8e8e8;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    flex-direction: column;
}
.er-tier-card:hover {
    border-color: #667eea;
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.15);
}
.er-tier-selected {
    border-color: #667eea;
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.25);
    background: #f8f9ff;
}
.er-tier-disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
.er-tier-disabled:hover {
    border-color: #e8e8e8;
    box-shadow: none;
}
.er-tier-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
}
.er-tier-name {
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
    font-size: 1.1rem;
}
.er-tier-price {
    font-weight: 800;
    font-size: 1.2rem;
    color: #667eea;
    white-space: nowrap;
}
.er-price-free {
    color: #27ae60;
}
.er-tier-desc {
    color: #777;
    font-size: 0.9rem;
    flex: 1;
}
.er-tier-footer {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}
.er-tier-availability {
    font-size: 0.85rem;
    color: #e67e22;
}
.er-tier-badge {
    font-size: 0.8rem;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 4px;
}
.er-badge-sold-out {
    background: #fdeaea;
    color: #e74c3c;
}
.er-badge-unavailable {
    background: #f5f5f5;
    color: #999;
}

/* Registration Form */
.er-form-section {
    max-width: 560px;
    margin: 0 auto;
}
.er-form-card {
    background: white;
    border-radius: 16px;
    padding: 36px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
}
.er-payment-info {
    background: #e8f4fd;
    border: 1px solid #bee5eb;
    border-radius: 8px;
    padding: 14px 16px;
    color: #0c5460;
    font-size: 0.95rem;
}
.er-submit-btn {
    background: linear-gradient(135deg, #667eea, #764ba2);
    border: none;
    font-weight: 600;
    font-size: 1.05rem;
    padding: 12px;
    border-radius: 10px;
}
.er-submit-btn:hover {
    opacity: 0.9;
}

/* Transitions */
.er-slide-enter-active,
.er-slide-leave-active {
    transition: all 0.3s ease;
}
.er-slide-enter,
.er-slide-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

/* Responsive */
@media (max-width: 768px) {
    .er-hero {
        min-height: 300px;
    }
    .er-hero-title {
        font-size: 1.8rem;
    }
    .er-details-grid {
        grid-template-columns: 1fr;
    }
    .er-sidebar-card {
        position: static;
    }
    .er-info-bar-inner {
        flex-direction: column;
        text-align: center;
    }
    .er-info-items {
        justify-content: center;
    }
    .er-tier-grid {
        grid-template-columns: 1fr;
    }
    .er-form-card {
        padding: 24px;
    }
}
@media (max-width: 480px) {
    .er-hero-title {
        font-size: 1.5rem;
    }
    .er-hero-overlay {
        padding: 40px 16px 24px;
    }
}
</style>
